import axios from 'axios';
import { getTenantFromPath } from './tenantDetection';

let suspensionModalCallback = null;

// Configure axios to send custom header for tenant detection
// Note: Browsers block setting 'Origin' header manually, so we use X-Frontend-Host instead
if (typeof window !== 'undefined') {
  // Send a custom header with the hostname for tenant detection
  axios.defaults.headers.common['X-Frontend-Host'] = window.location.hostname;
  
  // Also set up an interceptor to ensure it's always sent
  axios.interceptors.request.use((config) => {
    if (typeof window !== 'undefined') {
      config.headers['X-Frontend-Host'] = window.location.hostname;
      
      // For path-based tenants, also send X-Tenant-Identifier header
      const tenantFromPath = getTenantFromPath();
      if (tenantFromPath) {
        config.headers['X-Tenant-Identifier'] = tenantFromPath;
      } else {
        // For subdomain-based tenants, extract from hostname
        const hostname = window.location.hostname;
        const subdomain = hostname.split('.')[0];
        if (subdomain && subdomain !== 'localhost' && !subdomain.includes(':')) {
          config.headers['X-Tenant-Identifier'] = subdomain;
        }
      }
    }
    return config;
  });
}

/**
 * Set callback function to show suspension modal
 */
export const setSuspensionModalCallback = (callback) => {
  suspensionModalCallback = callback;
};

/**
 * Get suspension status from localStorage
 */
export const getSuspensionStatus = () => {
  try {
    const stored = localStorage.getItem('suspensionStatus');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error reading suspension status:', error);
  }
  return null;
};

/**
 * Set suspension status in localStorage
 */
export const setSuspensionStatus = (status, message, endDate) => {
  try {
    localStorage.setItem('suspensionStatus', JSON.stringify({
      status,
      message,
      endDate,
      timestamp: new Date().toISOString()
    }));
  } catch (error) {
    console.error('Error storing suspension status:', error);
  }
};

/**
 * Clear suspension status from localStorage
 */
export const clearSuspensionStatus = () => {
  try {
    localStorage.removeItem('suspensionStatus');
  } catch (error) {
    console.error('Error clearing suspension status:', error);
  }
};

/**
 * Setup axios interceptors to handle suspension/expiration errors
 */
export const setupAxiosInterceptors = () => {
  // Response interceptor to catch suspension/expiration errors
  axios.interceptors.response.use(
    (response) => {
      // If we get a successful response (200-299), check if account was reactivated
      const suspensionStatus = getSuspensionStatus();
      
      if (suspensionStatus && response.status >= 200 && response.status < 300) {
        // Check if response contains tenant info with active status
        if (response.data?.tenant?.status === 'active' || 
            (response.config?.url?.includes('/api/public/tenant') && response.data?.tenant?.status === 'active')) {
          // Account is active - clear suspension status
          clearSuspensionStatus();
          if (suspensionModalCallback) {
            suspensionModalCallback({
              isOpen: false,
              status: null,
              message: '',
              endDate: null
            });
          }
        } else if (response.status === 200 && 
                   response.config?.url && 
                   !response.config.url.includes('/api/public/') &&
                   !response.config.url.includes('/api/auth/login')) {
          // For authenticated endpoints (not public, not login), successful response means 
          // account passed subscription check middleware, so it's active
          // Clear suspension status if we get successful authenticated API calls
          clearSuspensionStatus();
          if (suspensionModalCallback) {
            suspensionModalCallback({
              isOpen: false,
              status: null,
              message: '',
              endDate: null
            });
          }
        }
      }
      
      return response;
    },
    (error) => {
      // Check if error is due to suspension or expiration
      if (
        error.response &&
        error.response.status === 403 &&
        (error.response.data?.status === 'suspended' || 
         error.response.data?.status === 'expired')
      ) {
        const { status, message, endDate } = error.response.data;
        
        // Store suspension status in localStorage for persistence
        setSuspensionStatus(status, message, endDate);
        
        // Show suspension modal if callback is set
        if (suspensionModalCallback) {
          suspensionModalCallback({
            isOpen: true,
            status,
            message: message || (
              status === 'suspended'
                ? 'Your account has been suspended. Please contact support for further assistance.'
                : 'Your subscription has expired. Please renew to continue using the platform.'
            ),
            endDate
          });
        }
      } else if (
        error.response &&
        error.response.status >= 200 &&
        error.response.status < 300
      ) {
        // If error response is actually successful (shouldn't happen, but handle it)
        const suspensionStatus = getSuspensionStatus();
        if (suspensionStatus) {
          clearSuspensionStatus();
          if (suspensionModalCallback) {
            suspensionModalCallback({
              isOpen: false,
              status: null,
              message: '',
              endDate: null
            });
          }
        }
      }
      
      // Return error so components can still handle it if needed
      return Promise.reject(error);
    }
  );
};
